/**
 * Hostel Service - API client for hostels, rooms, accommodation,
 * attendance, fees, maintenance, visitors, and complaints
 */
import api from '../utils/api';

const hostelService = {
  // ---- Hostels ----

  listHostels: async () => {
    try {
      return await api.get('/api/hostel/hostels');
    } catch (error) {
      console.error('Error fetching hostels:', error);
      throw error;
    }
  },

  createHostel: async (data) => {
    try {
      return await api.post('/api/hostel/hostels', data);
    } catch (error) {
      console.error('Error creating hostel:', error);
      throw error;
    }
  },

  updateHostel: async (hostelId, data) => {
    try {
      return await api.put(`/api/hostel/hostels/${hostelId}`, data);
    } catch (error) {
      console.error('Error updating hostel:', error);
      throw error;
    }
  },

  getHostelDeleteImpact: async (hostelId) => {
    try {
      return await api.get(`/api/hostel/hostels/${hostelId}/delete-impact`);
    } catch (error) {
      console.error('Error fetching hostel delete impact:', error);
      throw error;
    }
  },

  deleteHostel: async (hostelId) => {
    try {
      return await api.delete(`/api/hostel/hostels/${hostelId}`);
    } catch (error) {
      console.error('Error deleting hostel:', error);
      throw error;
    }
  },

  // ---- Rooms ----

  listRooms: async () => {
    try {
      return await api.get('/api/hostel/rooms');
    } catch (error) {
      console.error('Error fetching rooms:', error);
      throw error;
    }
  },

  createRoom: async (data) => {
    try {
      return await api.post('/api/hostel/rooms', data);
    } catch (error) {
      console.error('Error creating room:', error);
      throw error;
    }
  },

  updateRoom: async (roomId, data) => {
    try {
      return await api.put(`/api/hostel/rooms/${roomId}`, data);
    } catch (error) {
      console.error('Error updating room:', error);
      throw error;
    }
  },

  getRoomDeleteImpact: async (roomId) => {
    try {
      return await api.get(`/api/hostel/rooms/${roomId}/delete-impact`);
    } catch (error) {
      console.error('Error fetching room delete impact:', error);
      throw error;
    }
  },

  deleteRoom: async (roomId) => {
    try {
      return await api.delete(`/api/hostel/rooms/${roomId}`);
    } catch (error) {
      console.error('Error deleting room:', error);
      throw error;
    }
  },

  // ---- Room inventory (condition tracking) ----

  getRoomInventory: async (roomId) => {
    try {
      return await api.get(`/api/hostel/rooms/${roomId}/inventory`);
    } catch (error) {
      console.error('Error fetching room inventory:', error);
      throw error;
    }
  },

  createRoomInventoryItem: async (roomId, data) => {
    try {
      return await api.post(`/api/hostel/rooms/${roomId}/inventory`, data);
    } catch (error) {
      console.error('Error creating room inventory item:', error);
      throw error;
    }
  },

  updateRoomInventoryItem: async (itemId, data) => {
    try {
      return await api.put(`/api/hostel/inventory/${itemId}`, data);
    } catch (error) {
      console.error('Error updating room inventory item:', error);
      throw error;
    }
  },

  deleteRoomInventoryItem: async (itemId) => {
    try {
      return await api.delete(`/api/hostel/inventory/${itemId}`);
    } catch (error) {
      console.error('Error deleting room inventory item:', error);
      throw error;
    }
  },

  // ---- Accommodation (student allocations) ----

  listAccommodation: async () => {
    try {
      return await api.get('/api/hostel/accommodation');
    } catch (error) {
      console.error('Error fetching accommodation records:', error);
      throw error;
    }
  },

  createAccommodation: async (data) => {
    try {
      return await api.post('/api/hostel/accommodation', data);
    } catch (error) {
      console.error('Error creating accommodation record:', error);
      throw error;
    }
  },

  updateAccommodation: async (accommodationId, data) => {
    try {
      return await api.put(`/api/hostel/accommodation/${accommodationId}`, data);
    } catch (error) {
      console.error('Error updating accommodation record:', error);
      throw error;
    }
  },

  deleteAccommodation: async (accommodationId) => {
    try {
      return await api.delete(`/api/hostel/accommodation/${accommodationId}`);
    } catch (error) {
      console.error('Error deleting accommodation record:', error);
      throw error;
    }
  },

  // ---- Attendance ----

  listAttendance: async () => {
    try {
      return await api.get('/api/hostel/attendance');
    } catch (error) {
      console.error('Error fetching hostel attendance:', error);
      throw error;
    }
  },

  createAttendance: async (data) => {
    try {
      return await api.post('/api/hostel/attendance', data);
    } catch (error) {
      console.error('Error recording hostel attendance:', error);
      throw error;
    }
  },

  updateAttendance: async (attendanceId, data) => {
    try {
      return await api.put(`/api/hostel/attendance/${attendanceId}`, data);
    } catch (error) {
      console.error('Error updating hostel attendance:', error);
      throw error;
    }
  },

  deleteAttendance: async (attendanceId) => {
    try {
      return await api.delete(`/api/hostel/attendance/${attendanceId}`);
    } catch (error) {
      console.error('Error deleting hostel attendance:', error);
      throw error;
    }
  },

  // ---- Fee structures ----

  listFeeStructures: async () => {
    try {
      return await api.get('/api/hostel/fee-structures');
    } catch (error) {
      console.error('Error fetching hostel fee structures:', error);
      throw error;
    }
  },

  createFeeStructure: async (data) => {
    try {
      return await api.post('/api/hostel/fee-structures', data);
    } catch (error) {
      console.error('Error creating hostel fee structure:', error);
      throw error;
    }
  },

  updateFeeStructure: async (structureId, data) => {
    try {
      return await api.put(`/api/hostel/fee-structures/${structureId}`, data);
    } catch (error) {
      console.error('Error updating hostel fee structure:', error);
      throw error;
    }
  },

  deleteFeeStructure: async (structureId) => {
    try {
      return await api.delete(`/api/hostel/fee-structures/${structureId}`);
    } catch (error) {
      console.error('Error deleting hostel fee structure:', error);
      throw error;
    }
  },

  // ---- Fees ----

  listFees: async () => {
    try {
      return await api.get('/api/hostel/fees');
    } catch (error) {
      console.error('Error fetching hostel fees:', error);
      throw error;
    }
  },

  createFee: async (data) => {
    try {
      return await api.post('/api/hostel/fees', data);
    } catch (error) {
      console.error('Error creating hostel fee:', error);
      throw error;
    }
  },

  updateFee: async (feeId, data) => {
    try {
      return await api.put(`/api/hostel/fees/${feeId}`, data);
    } catch (error) {
      console.error('Error updating hostel fee:', error);
      throw error;
    }
  },

  deleteFee: async (feeId) => {
    try {
      return await api.delete(`/api/hostel/fees/${feeId}`);
    } catch (error) {
      console.error('Error deleting hostel fee:', error);
      throw error;
    }
  },

  // ---- Maintenance ----

  listMaintenance: async () => {
    try {
      return await api.get('/api/hostel/maintenance');
    } catch (error) {
      console.error('Error fetching hostel maintenance logs:', error);
      throw error;
    }
  },

  createMaintenance: async (data) => {
    try {
      return await api.post('/api/hostel/maintenance', data);
    } catch (error) {
      console.error('Error creating hostel maintenance log:', error);
      throw error;
    }
  },

  updateMaintenance: async (maintenanceId, data) => {
    try {
      return await api.put(`/api/hostel/maintenance/${maintenanceId}`, data);
    } catch (error) {
      console.error('Error updating hostel maintenance log:', error);
      throw error;
    }
  },

  deleteMaintenance: async (maintenanceId) => {
    try {
      return await api.delete(`/api/hostel/maintenance/${maintenanceId}`);
    } catch (error) {
      console.error('Error deleting hostel maintenance log:', error);
      throw error;
    }
  },

  // ---- Visitors ----

  listVisitors: async () => {
    try {
      return await api.get('/api/hostel/visitors');
    } catch (error) {
      console.error('Error fetching visitor logs:', error);
      throw error;
    }
  },

  createVisitor: async (data) => {
    try {
      return await api.post('/api/hostel/visitors', data);
    } catch (error) {
      console.error('Error creating visitor log:', error);
      throw error;
    }
  },

  updateVisitor: async (visitorId, data) => {
    try {
      return await api.put(`/api/hostel/visitors/${visitorId}`, data);
    } catch (error) {
      console.error('Error updating visitor log:', error);
      throw error;
    }
  },

  deleteVisitor: async (visitorId) => {
    try {
      return await api.delete(`/api/hostel/visitors/${visitorId}`);
    } catch (error) {
      console.error('Error deleting visitor log:', error);
      throw error;
    }
  },

  // ---- Complaints ----

  listComplaints: async () => {
    try {
      return await api.get('/api/hostel/complaints');
    } catch (error) {
      console.error('Error fetching complaints:', error);
      throw error;
    }
  },

  createComplaint: async (data) => {
    try {
      return await api.post('/api/hostel/complaints', data);
    } catch (error) {
      console.error('Error creating complaint:', error);
      throw error;
    }
  },

  updateComplaintStatus: async (complaintId, status, resolutionNotes) => {
    try {
      return await api.put(`/api/hostel/complaints/${complaintId}`, {
        status, resolution_notes: resolutionNotes || null
      });
    } catch (error) {
      console.error('Error updating complaint:', error);
      throw error;
    }
  },

  deleteComplaint: async (complaintId) => {
    try {
      return await api.delete(`/api/hostel/complaints/${complaintId}`);
    } catch (error) {
      console.error('Error deleting complaint:', error);
      throw error;
    }
  }
};

export default hostelService;
